import type { ApiProblem } from '@rental/contracts/client'
import {
  type CheckoutBlock,
  type CheckoutBlocks,
  HARD_CHECKOUT_BLOCKS,
  isHardCheckoutBlock,
  OVERRIDABLE_CHECKOUT_BLOCKS,
} from '@rental/domain'

/** The domain's own order: what nobody overrides first, then what the Owner may. */
const BLOCK_ORDER: readonly CheckoutBlock[] = [
  ...HARD_CHECKOUT_BLOCKS,
  ...OVERRIDABLE_CHECKOUT_BLOCKS,
]

/** What the server named when it last refused the hand-over for its blocks (rentals.ts). */
export interface ServerBlocks {
  blocks: readonly CheckoutBlock[]
  hard: readonly CheckoutBlock[]
}

function knownBlocks(value: unknown): CheckoutBlock[] {
  return Array.isArray(value) ? BLOCK_ORDER.filter((block) => value.includes(block)) : []
}

/**
 * The blocks a 422 `checkout_blocked` names, in the domain's order; null for
 * any other answer. A block this version of the app does not know is left
 * out: the refusal itself still shows, in words, beside the button.
 */
export function serverBlocks(problem: ApiProblem): ServerBlocks | null {
  if (problem.kind !== 'api' || problem.code !== 'checkout_blocked') return null
  return { blocks: knownBlocks(problem.details.blocks), hard: knownBlocks(problem.details.hard) }
}

/** The mileage as typed at the counter: whole kilometres, at most seven digits. */
export function readingKm(km: string): number | null {
  return /^\d{1,7}$/.test(km) ? Number(km) : null
}

/**
 * What the hand-over form itself still lacks, each said beside the button
 * (DESIGN.md: never a disabled button with no reason), in the page's order.
 * The blocks are not here: their own notices say them, once.
 */
export type FormReason =
  | 'terms'
  | 'early'
  | 'late'
  | 'vehicle'
  | 'km'
  | 'fuel'
  | 'reading'
  | 'unsent'
  | 'unsentReason'
  | 'documents'
  | 'overrideReason'

export interface HandOverForm {
  now: number
  opensAt: string
  endsAt: string
  /** C1: no terms in force for the contract the car would leave on. */
  stopped: boolean
  vehicleId: string
  km: string
  fuel: string
  documents: boolean
  /** S4: this iPad's items of this inspection not yet on the server. */
  inspection: { known: boolean; unsent: number; leaving: boolean; leaveReason: string }
  /** The Owner's override of the blocks the Owner may override. */
  override: { chosen: boolean; reason: string }
}

export interface Readiness {
  reasons: FormReason[]
  /** Blocks nobody overrides (decisions 1 and 5). */
  hard: CheckoutBlock[]
  /** Blocks the Owner overrides, with a reason. */
  overridable: CheckoutBlock[]
  /** The override goes with the request: blocks to override, and the Owner's reason. */
  overriding: boolean
  ready: boolean
}

const given = (reason: string) => reason.trim().length >= 3

/**
 * S4: what the inspection's photos still hold up, at the hand-over and at the
 * return alike: this iPad's list being read, items not yet sent, or the
 * Owner's choice to go without them still lacking its reason.
 */
export function inspectionReason(
  inspection: HandOverForm['inspection'],
): 'reading' | 'unsent' | 'unsentReason' | null {
  if (!inspection.known) return 'reading'
  if (inspection.unsent === 0) return null
  if (!inspection.leaving) return 'unsent'
  return given(inspection.leaveReason) ? null : 'unsentReason'
}

/**
 * Whether the vehicle can leave, as the server will answer it, and why not.
 *
 * The blocks are the screen's own (packages/domain, the server's rules) joined
 * with those the server named when it last refused: the screen can miss one
 * (facts it has not read yet, a rule a newer server reads differently), and
 * then the refusal must not stay a riddle. A block hard on either side is hard.
 *
 * Money owed and the deposit are not reasons: the server hands the car over
 * without either (Q-63's deposit block aside, which is a block).
 */
export function readiness(
  form: HandOverForm,
  client: CheckoutBlocks,
  server: ServerBlocks | null,
): Readiness {
  const named = new Set<CheckoutBlock>([...client.blocks, ...(server?.blocks ?? [])])
  for (const block of server?.hard ?? []) named.add(block)
  const hardOnServer = new Set(server?.hard ?? [])
  const blocks = BLOCK_ORDER.filter((block) => named.has(block))
  const isHard = (block: CheckoutBlock) => isHardCheckoutBlock(block) || hardOnServer.has(block)
  const hard = blocks.filter(isHard)
  const overridable = blocks.filter((block) => !isHard(block))

  const reasons: FormReason[] = []
  if (form.stopped) reasons.push('terms')
  if (form.now < Date.parse(form.opensAt)) reasons.push('early')
  else if (form.now >= Date.parse(form.endsAt)) reasons.push('late')
  if (form.vehicleId === '') reasons.push('vehicle')
  if (readingKm(form.km) === null) reasons.push('km')
  if (form.fuel === '') reasons.push('fuel')
  const inspection = inspectionReason(form.inspection)
  if (inspection) reasons.push(inspection)
  if (!form.documents) reasons.push('documents')
  const chosen = overridable.length > 0 && form.override.chosen
  if (chosen && !given(form.override.reason)) reasons.push('overrideReason')
  const overriding = chosen && given(form.override.reason)

  return {
    reasons,
    hard,
    overridable,
    overriding,
    ready: reasons.length === 0 && hard.length === 0 && (overridable.length === 0 || overriding),
  }
}
